import { useCallback, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  BarChart3,
  Brain,
  ChevronDown,
  ClipboardList,
  Clock,
  Lightbulb,
  Loader2,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import HeroStatusChip from "@/components/HeroStatusChip";
import { splitIntoInsightBuckets } from "@/components/AiInsightsCard";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import { cardsToInsightLines, toInsightLines } from "@/utils/insightActionPlanDefaults";
import { getBackendUrl } from "@/lib/backend";
import { useScope } from "@/context/ScopeContext";
import { useMistralFirstInsights } from "@/hooks/useMistralFirstInsights";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import {
  buildAadhaarExecutiveSummary,
  buildAadhaarInsightCards,
  buildAadhaarInsightsMarkdown,
  buildAadhaarPriorityActions,
  buildAadhaarPrioritySchools,
  buildAadhaarRecommendationBullets,
  buildAadhaarRecommendations,
  buildAadhaarRootCauses,
  buildAadhaarRootCauseSignals,
  countAadhaarPrioritySignals,
  deriveAadhaarInsightConfidence,
  deriveAadhaarOverallConfidence,
  fmt,
  pct,
} from "@/utils/aadhaarDerived";
import "./AadhaarInsightsTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const API = `${getBackendUrl()}/api`;

const CARD_META = [
  { id: "coverage", signal: "Positive", signalClass: "", iconClass: "green", barClass: "" },
  { id: "mismatch", signal: "Risk", signalClass: "red", iconClass: "red", barClass: "red" },
  { id: "mbu", signal: "Bottleneck", signalClass: "purple", iconClass: "purple", barClass: "purple" },
  { id: "blocks", signal: "Action", signalClass: "amber", iconClass: "orange", barClass: "orange" },
];

const bulletsToCards = (bullets, overview) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 4).map((text, i) => {
    const meta = CARD_META[i] || CARD_META[0];
    const title = text.split(/[:.]/)[0].slice(0, 55) || `Insight ${i + 1}`;
    return {
      ...meta,
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      body: text,
      confidence: deriveAadhaarInsightConfidence(overview, i),
    };
  });
};

const bulletsToRecommendations = (bullets) => {
  if (!bullets?.length) return null;
  const tones = ["red", "purple", "orange", "green"];
  return bullets.slice(0, 4).map((body, i) => {
    const title = body.split(/[:.]/)[0].slice(0, 50) || `Action ${i + 1}`;
    return {
      num: String(i + 1).padStart(2, "0"),
      tone: tones[i] || "orange",
      title: title.length > 48 ? `${title.slice(0, 45)}…` : title,
      body,
    };
  });
};

const DEFAULT_PROMPT =
  "Which blocks should be targeted first to reduce Aadhaar exceptions within 7 days?";

const SUGGESTED_PROMPTS = [
  {
    label: "Explain MBU bottleneck by block",
    prompt: "Explain why MBU pending is high and which blocks need camps first.",
  },
  {
    label: "Create 7-day closure plan",
    prompt: "Create a 7-day closure plan for bottom 10 coverage blocks.",
  },
  {
    label: "Find low-enrolment high-risk schools",
    prompt: "List schools where small enrolment is inflating the exception rate.",
  },
  {
    label: "Diagnose name mismatch cases",
    prompt: "Show root causes behind name mismatch and recommended field validation steps.",
  },
];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const CARD_ICONS = {
  coverage: TrendingUp,
  mismatch: AlertTriangle,
  mbu: Clock,
  blocks: ClipboardList,
};

export default function AadhaarInsightsTab({
  overview,
  blockData = [],
  highRiskSchools = [],
}) {
  const { scope } = useScope();
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [copilotResponse, setCopilotResponse] = useState("");

  const scopeParams = useMemo(
    () => ({
      district_code: scope.districtCode || undefined,
      block_code: scope.blockCode || undefined,
      udise_code: scope.udiseCode || undefined,
    }),
    [scope.districtCode, scope.blockCode, scope.udiseCode]
  );

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        v: scope.version,
        e: overview?.total_enrolment,
        c: overview?.aadhaar_coverage_pct,
      }),
    [scope.version, overview?.total_enrolment, overview?.aadhaar_coverage_pct]
  );

  const buildLocalMarkdown = useCallback(
    () => buildAadhaarInsightsMarkdown(overview, blockData, highRiskSchools),
    [overview, blockData, highRiskSchools]
  );

  const {
    markdown: aiMarkdown,
    provider,
    loading: insightsLoading,
    generatedAt,
    refresh: refreshInsights,
    isMistral,
  } = useMistralFirstInsights({
    kind: "aadhaar",
    title: "Aadhaar Analytics Insights",
    buildLocalMarkdown,
    context: { overview, blockData, highRiskSchools },
    enabled: Boolean(overview?.total_enrolment),
    scopeKey,
  });

  const buckets = useMemo(() => splitIntoInsightBuckets(aiMarkdown), [aiMarkdown]);

  const studentsToTarget = useMemo(() => {
    const total = overview?.total_enrolment ?? 0;
    const passed = overview?.aadhaar_passed ?? 0;
    return Math.max(0, total - passed);
  }, [overview]);

  const localInsightCards = useMemo(
    () => buildAadhaarInsightCards(overview, blockData),
    [overview, blockData]
  );
  const insightCards = useMemo(() => {
    if (insightsLoading) return [];
    const aiCards = bulletsToCards(parseInsightBullets(buckets.insights), overview);
    return aiCards?.length ? aiCards : localInsightCards;
  }, [insightsLoading, buckets.insights, overview, localInsightCards]);

  const rootCauses = useMemo(() => buildAadhaarRootCauses(overview), [overview]);
  const localRecommendations = useMemo(
    () => buildAadhaarRecommendations(overview, blockData),
    [overview, blockData]
  );
  const recommendations = useMemo(() => {
    if (insightsLoading) return [];
    const aiRecs = bulletsToRecommendations(parseInsightBullets(buckets.recs));
    return aiRecs?.length ? aiRecs : localRecommendations;
  }, [insightsLoading, buckets.recs, localRecommendations]);

  const localExecutiveSummary = useMemo(() => buildAadhaarExecutiveSummary(overview), [overview]);
  const executiveSummary = useMemo(() => {
    if (insightsLoading) return ["Generating AI insights…"];
    const bullets = parseInsightBullets(buckets.insights);
    return bullets.length ? bullets : localExecutiveSummary;
  }, [insightsLoading, buckets.insights, localExecutiveSummary]);

  const localRootCauseSignals = useMemo(() => buildAadhaarRootCauseSignals(overview), [overview]);
  const rootCauseSignals = useMemo(() => {
    if (insightsLoading) return ["Generating AI insights…"];
    const bullets = parseInsightBullets(buckets.root);
    return bullets.length ? bullets : localRootCauseSignals;
  }, [insightsLoading, buckets.root, localRootCauseSignals]);

  const localRecommendationBullets = useMemo(
    () => buildAadhaarRecommendationBullets(overview, blockData),
    [overview, blockData]
  );
  const recommendationBullets = useMemo(() => {
    if (insightsLoading) return ["Generating AI insights…"];
    const bullets = parseInsightBullets(buckets.recs);
    return bullets.length ? bullets : localRecommendationBullets;
  }, [insightsLoading, buckets.recs, localRecommendationBullets]);

  const localPriorityActions = useMemo(
    () => buildAadhaarPriorityActions(overview, highRiskSchools),
    [overview, highRiskSchools]
  );
  const priorityActions = useMemo(() => {
    if (insightsLoading) return ["Generating AI insights…"];
    const bullets = parseInsightBullets(buckets.actions);
    return bullets.length ? bullets : localPriorityActions;
  }, [insightsLoading, buckets.actions, localPriorityActions]);
  const prioritySchools = useMemo(
    () => buildAadhaarPrioritySchools(highRiskSchools),
    [highRiskSchools]
  );

  const defaultRoot = useMemo(() => toInsightLines(localRootCauseSignals), [localRootCauseSignals]);
  const defaultRecs = useMemo(() => toInsightLines(localRecommendationBullets), [localRecommendationBullets]);
  const defaultActions = useMemo(() => toInsightLines(localPriorityActions), [localPriorityActions]);

  const overallConfidence = useMemo(
    () => deriveAadhaarOverallConfidence(overview, blockData),
    [overview, blockData]
  );
  const prioritySignalCount = useMemo(
    () => countAadhaarPrioritySignals(overview, blockData),
    [overview, blockData]
  );

  const handleGenerateInsights = useCallback(async () => {
    const usedProvider = await refreshInsights();
    toast.success(
      usedProvider === "mistral"
        ? "Insights refreshed with Mistral AI."
        : "Insights refreshed (rule-based fallback)."
    );
  }, [refreshInsights]);

  const handleCopilotGenerate = useCallback(async () => {
    setCopilotLoading(true);
    const localMarkdown = buildAadhaarInsightsMarkdown(overview, blockData, highRiskSchools);
    try {
      const response = await axios.post(
        `${API}/analytics/insights/generate`,
        {
          kind: "aadhaar-copilot",
          title: "Aadhaar Copilot",
          local_markdown: localMarkdown,
          context: { overview, blockData, highRiskSchools, prompt },
        },
        { params: scopeParams }
      );
      const text = response?.data?.ai_insights || localMarkdown;
      setCopilotResponse(text);
      toast.success(
        response?.data?.provider === "mistral"
          ? "Copilot response from Mistral."
          : "Copilot response (rule-based fallback)."
      );
    } catch {
      setCopilotResponse(localMarkdown);
      toast.success("Copilot response (rule-based fallback).");
    } finally {
      setCopilotLoading(false);
    }
  }, [overview, blockData, highRiskSchools, prompt, scopeParams]);

  if (!overview?.total_enrolment) {
    return (
      <div className="aa-insights-empty">
        <Brain size={40} style={{ margin: "0 auto 12px", opacity: 0.4 }} />
        <p>No insights data available for the selected scope.</p>
      </div>
    );
  }

  const accordionStatus = insightsLoading
    ? "Generating…"
    : generatedAt
      ? isMistral
        ? "Mistral AI"
        : "Rule-based fallback"
      : "Ready";

  return (
    <div className="aa-insights-tab">
      <section className="aa-insights-hero" id="aa-insights-hero">
        <div className="aa-insights-hero-layout">
          <div className="aa-insights-ai-header">
            <div className="aa-insights-ai-orb" aria-hidden="true">
              <Lightbulb strokeWidth={1.8} />
            </div>
            <div className="aa-insights-ai-copy">
              <h2>Aadhaar Intelligence Center</h2>
              <p>
                Mistral-powered insights translate coverage, exception, mismatch and MBU patterns
                into root causes, confidence scores and officer-ready actions.
              </p>
              <div className="aa-insights-status-row">
                <HeroStatusChip
                  chipClass="aa-insights-status-chip"
                  variant={isMistral ? "purple" : "green"}
                  showChevron={false}
                >
                  {insightsLoading ? "Generating…" : isMistral ? "Mistral AI" : "Rule-based"}
                </HeroStatusChip>
                <HeroStatusChip chipClass="aa-insights-status-chip" variant="purple" showChevron={false}>
                  AI confidence {overallConfidence}%
                </HeroStatusChip>
                <HeroStatusChip
                  chipClass="aa-insights-status-chip"
                  variant="amber"
                  onClick={prioritySignalCount > 0 ? () => scrollToId("aa-insights-queue") : undefined}
                  ariaLabel={
                    prioritySignalCount > 0
                      ? `${prioritySignalCount} priority signals — view insight action queue`
                      : undefined
                  }
                >
                  {prioritySignalCount} priority signal{prioritySignalCount === 1 ? "" : "s"}
                </HeroStatusChip>
              </div>
            </div>
          </div>

          <div className="aa-insights-metrics" aria-label="Aadhaar insight metrics">
            <article className="aa-insights-mini-kpi success">
              <small>Current Coverage</small>
              <strong>{pct(overview.aadhaar_coverage_pct)}</strong>
              <span>
                {overview.aadhaar_coverage_pct >= 95 ? "Strong base" : "Needs improvement"};{" "}
                {fmt(studentsToTarget)} students to 100%
              </span>
            </article>
            <article className="aa-insights-mini-kpi danger">
              <small>Exception Rate</small>
              <strong>{pct(overview.aadhaar_exception_pct)}</strong>
              <span>{fmt(overview.aadhaar_failed)} failed records require validation</span>
            </article>
            <article className="aa-insights-mini-kpi warning">
              <small>Name Mismatch</small>
              <strong>{pct(overview.name_mismatch_pct)}</strong>
              <span>Highest quality risk in student identity</span>
            </article>
            <article className="aa-insights-mini-kpi purple">
              <small>MBU Pending</small>
              <strong>{pct(overview.mbu_pending_total_pct)}</strong>
              <span>{fmt(overview.mbu_pending_total)} students pending biometric updates</span>
            </article>
          </div>
        </div>
      </section>

      <div className="aa-insights-section-title">
        <div>
          <h2>AI-Generated Insight Cards</h2>
          <p>
            Important signals are separated into cards, so officers can immediately understand what
            is good, what is risky, where to act, and why it matters.
          </p>
        </div>
        <button
          type="button"
          className="aa-insights-section-action"
          onClick={handleGenerateInsights}
          disabled={insightsLoading}
        >
          {insightsLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          Generate latest insights →
        </button>
      </div>

      <section className="aa-insights-grid">
        {insightsLoading && insightCards.length === 0 ? (
          <article className="aa-insights-card" style={{ gridColumn: "1 / -1" }}>
            <p style={{ margin: 0, opacity: 0.7 }}>
              <Loader2 size={16} className="animate-spin" style={{ display: "inline", marginRight: 8 }} />
              Generating AI insight cards…
            </p>
          </article>
        ) : null}
        {insightCards.map((card) => {
          const Icon = CARD_ICONS[card.id] || Lightbulb;
          return (
            <article key={card.id} className="aa-insights-card">
              <div className="aa-insights-card-top">
                <div className={`aa-insights-icon-box ${card.iconClass}`}>
                  <Icon size={22} />
                </div>
                <span className={`aa-insights-signal ${card.signalClass}`}>{card.signal}</span>
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <p>{card.body}</p>
              <div className={`aa-insights-confidence ${card.barClass}`}>
                <i style={{ width: `${card.confidence}%` }} />
              </div>
              <div className="aa-insights-card-meta">
                <span>Confidence</span>
                <b>{card.confidence}%</b>
              </div>
            </article>
          );
        })}
      </section>

      <div className="aa-insights-section-title">
        <div>
          <h2>Root-Cause Diagnostics</h2>
          <p>
            Instead of showing raw AI text, the insight tab converts diagnosis into explainable
            causes and specific recommendations.
          </p>
        </div>
        <button
          type="button"
          className="aa-insights-section-action"
          onClick={() => scrollToId("aa-insights-queue")}
        >
          View action queue →
        </button>
      </div>

      <section className="aa-insights-analytics-grid">
        <article className="aa-insights-panel">
          <div className="aa-insights-panel-head">
            <div>
              <h3>Root-Cause Signal Strength</h3>
              <p>Relative strength of each signal based on failed, pending, mismatch and MBU patterns.</p>
            </div>
            <span className="aa-insights-pill">Explainable AI</span>
          </div>
          <div className="aa-insights-cause-list">
            {rootCauses.map((row) => (
              <div key={row.label} className="aa-insights-cause-row">
                <div className="aa-insights-cause-label">{row.label}</div>
                <div className="aa-insights-cause-track">
                  <div
                    className={`aa-insights-cause-bar ${row.tone}`}
                    style={{ width: `${row.width}%` }}
                  />
                </div>
                <div className="aa-insights-cause-value">{row.value}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="aa-insights-panel">
          <div className="aa-insights-panel-head">
            <div>
              <h3>Recommended Interventions</h3>
              <p>Priority-ranked actions generated from current coverage and exception patterns.</p>
            </div>
            <span className="aa-insights-pill">Next best action</span>
          </div>
          <div className="aa-insights-rec-stack">
            {recommendations.map((rec) => (
              <div key={rec.num} className="aa-insights-rec">
                <div className={`aa-insights-num-dot ${rec.tone}`}>{rec.num}</div>
                <div>
                  <b>{rec.title}</b>
                  <span>{rec.body}</span>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="aa-insights-action-plan">
        <InterventionActionPlan
          aiContent={aiMarkdown}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <div className="aa-insights-section-title" id="aa-insights-queue">
        <div>
          <h2>Priority School Action Queue</h2>
          <p>AI output is translated into owner-ready tasks with risk label, likely reason and next step.</p>
        </div>
        <button
          type="button"
          className="aa-insights-section-action"
          onClick={() => scrollToId("aa-insights-hero")}
        >
          Back to top ↑
        </button>
      </div>

      <section className="aa-insights-two-col">
        <article className="aa-insights-panel aa-insights-table-card">
          <div className="aa-insights-table-head">
            <div>
              <h3>Top AI-Prioritized Schools</h3>
              <p>Ranked by exception rate, enrolment context and closure feasibility.</p>
            </div>
            <span className="aa-insights-pill">Top {prioritySchools.length || 8}</span>
          </div>
          <div className="aa-insights-table-wrap">
            <table className="aa-insights-table" aria-label="Top AI-prioritized schools">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>School</th>
                  <th>Block</th>
                  <th>Signal</th>
                  <th>Likely Reason</th>
                  <th>Next Action</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                {prioritySchools.length === 0 ? (
                  <tr>
                    <td colSpan={7}>No high-risk schools in current scope.</td>
                  </tr>
                ) : (
                  prioritySchools.map((row) => (
                    <tr key={`${row.school}-${row.priority}`}>
                      <td>
                        <span className={`aa-insights-risk-badge ${row.badgeTone}`}>{row.priority}</span>
                      </td>
                      <td>{row.school}</td>
                      <td>{row.block}</td>
                      <td>
                        <span className={`aa-insights-tag ${row.tagTone}`}>{row.signalTag}</span>
                      </td>
                      <td>{row.reason}</td>
                      <td>{row.action}</td>
                      <td>{row.owner}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="aa-insights-panel aa-insights-copilot">
          <div className="aa-insights-copilot-header">
            <h3>Ask Aadhaar Insights Copilot</h3>
            <p>
              Use this panel for district review questions. It keeps the page action-oriented instead
              of showing a long static text dump.
            </p>
          </div>
          <div className="aa-insights-prompt-box">
            <label htmlFor="aadhaar-copilot-prompt">Question for AI</label>
            <textarea
              id="aadhaar-copilot-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </div>
          <div className="aa-insights-prompt-chips">
            {SUGGESTED_PROMPTS.map((chip) => (
              <button
                key={chip.label}
                type="button"
                className="aa-insights-prompt-chip"
                onClick={() => setPrompt(chip.prompt)}
              >
                {chip.label}
              </button>
            ))}
            <button
              type="button"
              className="aa-insights-generate-btn"
              onClick={handleCopilotGenerate}
              disabled={copilotLoading}
            >
              {copilotLoading ? <Loader2 size={18} className="animate-spin" /> : <Lightbulb size={18} />}
              Generate Response
            </button>
          </div>
          {copilotResponse ? (
            <div className="aa-insights-copilot-response">{copilotResponse}</div>
          ) : null}
        </article>
      </section>
    </div>
  );
}
